import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { clone, isEmpty, pluck } from 'underscore';
import {
  Button, Combobox, Link, Option, Text,
  TableBody, TableCell, TableRow, Table, TableHeader, TableHeaderCell, TableCellLayout,
  makeStyles, shorthands, tokens, useId,
} from '@fluentui/react-components';
import { DeleteRegular } from '@fluentui/react-icons';
import { useMultiSelectContext, withMultiSelectCombobox } from '@bingads-webui-campaign-react/multi-selector';
import { useDebouncedState } from '@bingads-webui-campaign-react/common-generic-hooks';
import { InlineLoading, LoadingOverlay } from '@bingads-webui-campaign-react/loading';
import { getRoutePathById, allSiteMap } from '@bingads-webui-campaign-react/with-site-map';

const SEARCH_START_DELAY_MS = 500;
const TABLE_COLUMNS = [
  { key: 'audience_name', label: 'Audience list' },
  { key: 'audience_type', label: 'Audience type' },
];
const MultiSelectCombobox = withMultiSelectCombobox(Combobox);

const getAudiencesLibraryUrl = (cid, aid) => {
  const audiencesLibraryPath = getRoutePathById(allSiteMap, 'audiences-library');
  const searchParams = `?cid=${cid}&aid=${aid}`;
  return `${audiencesLibraryPath}${searchParams}`;
};

export const useAudienceListSectionStyle = makeStyles({
  audienceListSection: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
    '& .list-number-text': {
      color: tokens.colorNeutralForeground2Link,
    },
  },
  combobox: {
    width: '100%',
  },
  listbox: {
    maxHeight: '345px',
  },
  TypeInContent: {
    ...shorthands.margin('12px'),
    fontSize: '14px',
    lineHeight: '20px',
  },
  NonOptionContent: {
    ...shorthands.margin('12px'),
    '& .audience-empty-header': {
      fontSize: '14px',
      lineHeight: '20px',
    },
  },
  audienceTable: {
    marginTop: '12px',
    '& .delete-button': {
      justifyContent: 'flex-end',
    },
  },
});

export const AudienceListSection = (props) => {
  const {
    i18n, isAccountLevel, cid, aid,
  } = props;
  const styles = useAudienceListSectionStyle();
  const comboboxId = useId('combobox');
  const {
    isLoadingOptions,
    isFirstOptionsLoad,
    options,
    optionsLoadError,
    lastLoadedSearchFilter,

    isLoadingSelectedOptions,
    isSelectedOptionsLimitReached,
    selectedOptions,
    selectedOptionsLoadError,
    setSelectedOptions,

    setSearchFilter: setSearchFilterFromContext,
  } = useMultiSelectContext();

  const isAnyLoading = isLoadingOptions || isLoadingSelectedOptions;
  const hasLoadErrors = optionsLoadError || selectedOptionsLoadError;
  const showInitialLoading = (isFirstOptionsLoad && !hasLoadErrors) || isLoadingSelectedOptions;
  const canUserStartOperating = !showInitialLoading && !selectedOptionsLoadError;
  const selectedOptionKeys = useMemo(() => pluck(selectedOptions, 'key'), [selectedOptions]);
  const selectedOptionsSet = useMemo(() => new Set(selectedOptionKeys), [selectedOptionKeys]);
  const shouldDisableSelection = useCallback(option => isSelectedOptionsLimitReached && !selectedOptionsSet.has(option.key), [isSelectedOptionsLimitReached, selectedOptionsSet]);

  const [debouncedSearchFilter, setSearchFilter, searchFilter] = useDebouncedState('', SEARCH_START_DELAY_MS);
  useEffect(() => {
    setSearchFilterFromContext(debouncedSearchFilter);
  }, [debouncedSearchFilter, setSearchFilterFromContext]);
  const onSearchInputChange = useCallback((event) => {
    const { value: rawValue } = event.target;
    setSearchFilter(rawValue);
  }, [setSearchFilter]);
  const isSearchFilterEmpty = !lastLoadedSearchFilter;

  const [isComboboxOpen, setIsComboboxOpen] = useState(false);
  const onOpenChange = useCallback((event, data) => {
    setIsComboboxOpen(data.open);
  }, []);
  const onOptionSelect = useCallback((event, data) => {
    setSelectedOptions(data.resolvedSelectedOptions);
  }, [setSelectedOptions]);
  const onDeleteItem = useCallback((index) => {
    const newSelectedOptions = clone(selectedOptions);
    newSelectedOptions.splice(index, 1);
    setSelectedOptions(newSelectedOptions);
  }, [selectedOptions, setSelectedOptions]);

  const renderMultiSelectCombobox = useCallback(() => (
    <MultiSelectCombobox
      id={comboboxId}
      placeholder={i18n.getString(_TL_('Search in your audience segments'))}
      listbox={{ className: styles.listbox }}
      root={{ className: styles.combobox }}
      disabled={!canUserStartOperating}
      onOptionSelect={onOptionSelect}
      onChange={onSearchInputChange}
      value={searchFilter}
      options={options}
      selectedOptions={selectedOptions}
      open={isComboboxOpen}
      onOpenChange={onOpenChange}
    >
      <LoadingOverlay isVisible={isLoadingOptions} flexDirection="column">
        <>
          {isSearchFilterEmpty && isEmpty(options) && (
            <div className={styles.TypeInContent}>
              {i18n.getString(_TL_('Start typing to search for audiences'))}
            </div>
          )}
          {!isSearchFilterEmpty && isEmpty(options) && (
            <div className={styles.NonOptionContent}>
              <div className="audience-empty-header">
                {i18n.getString(_TL_('No audience found'))}
              </div>
            </div>
          )}
          {!isEmpty(options) && options.map(option => (
            <Option key={option.key} value={option.key} text={option.text} disabled={isLoadingOptions || shouldDisableSelection(option)} >
              {option.text}
            </Option>
          ))}
        </>
      </LoadingOverlay>
    </MultiSelectCombobox>
  ), [canUserStartOperating, comboboxId, i18n, isLoadingOptions, isComboboxOpen, isSearchFilterEmpty, onOpenChange, onOptionSelect, onSearchInputChange, options, searchFilter, selectedOptions, shouldDisableSelection, styles.NonOptionContent, styles.TypeInContent, styles.combobox, styles.listbox]);

  const renderTable = useCallback(() => (
    <Table className={styles.audienceTable}>
      <TableHeader>
        <TableRow>
          { TABLE_COLUMNS.map(column => (
            <TableHeaderCell key={column.key}>
              <Text size={200} weight="bold">{i18n.getString(_TL_('{{label}}'), { label: column.label })}</Text>
            </TableHeaderCell>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        { selectedOptions.map((option, index) => {
          const audienceName = option.text;
          let audienceType = option.type;
          if (!audienceType) {
            const found = options.find(o => o.key === option.key);
            audienceType = found ? found.type : ''; // Temporary fix for the case where the selected option is not included in the options list
          }
          return (
            <TableRow key={option.key}>
              <TableCell>{audienceName}</TableCell>
              <TableCell>{audienceType}</TableCell>
              { isAccountLevel && (
                <TableCell>
                  <TableCellLayout className="delete-button">
                    <Button
                      appearance="transparent"
                      icon={<DeleteRegular />}
                      onClick={() => onDeleteItem(index)}
                    />
                  </TableCellLayout>
                </TableCell>
              )}
            </TableRow>
          );
        })
      }
      </TableBody>
    </Table>
  ), [i18n, isAccountLevel, onDeleteItem, options, selectedOptions, styles.audienceTable]);

  return (
    <div className={styles.audienceListSection}>
      <Text size={400} weight="bold">{i18n.getString(_TL_('Existing audience lists'))}</Text>
      <Text size={300}>
        {i18n.getString(_TL_('Add an audience list to help us identify potential new customers.'))}
        <Text size={300} italic>{i18n.getString(_TL_(' Note: '))}</Text>
        {i18n.getString(_TL_('You can update your audience lists in '))}
        <Link href={getAudiencesLibraryUrl(cid, aid)} target="_blank">{i18n.getString(_TL_('Audiences'))}</Link>.
      </Text>
      { isAnyLoading && isFirstOptionsLoad && <InlineLoading text={i18n.getString(_TL_('Loading...'))} />}
      { isAccountLevel && canUserStartOperating && renderMultiSelectCombobox() }
      { selectedOptions.length > 0 && renderTable() }
      <Text size={300} className="list-number-text">{selectedOptions.length} {i18n.getString(_TL_('segment'))}</Text>
    </div>
  );
};

AudienceListSection.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  isAccountLevel: PropTypes.bool.isRequired,
  cid: PropTypes.number.isRequired,
  aid: PropTypes.number.isRequired,
};
